import { Stack, Typography, FormGroup, FormControlLabel, Checkbox } from "@mui/material";
import { TimePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { useCallback, useMemo } from "react";
import { ScheduleDay, DayOfWeek } from "../../../../../types/actionModule/schedulingStrategy/schedulingStrategyType";
import { useTranslation } from "react-i18next";

export interface ScheduleDaysDefinitionProps {
    dayModel: ScheduleDay[];
    onUpdateDayModel: (value: ScheduleDay[]) => void;
}

export function ScheduleDaysDefinitionEditor(props: ScheduleDaysDefinitionProps) {
    const { t } = useTranslation();
    const builder = useCallback((dayOfWeek: DayOfWeek) => {
        const existingModel = props.dayModel.find((m) => m.dayOfWeek === dayOfWeek);
        const model = existingModel ?? { dayOfWeek: dayOfWeek, startTime: dayjs().startOf("day"), endTime: dayjs().endOf("day") };

        const onUpdateDayModel = (model: ScheduleDay) => {
            props.onUpdateDayModel(
                props.dayModel.map((m) => m === existingModel ? model : m)
            )
        }
        const onAddDayModel = () => {
            props.onUpdateDayModel([
                ...props.dayModel,
                model,
            ])
        }
        const onRemoveDayModel = () => {
            props.onUpdateDayModel(props.dayModel.filter((p) => p.dayOfWeek !== dayOfWeek))
        }

        return <ScheduleDayFormGroup
            key={dayOfWeek}
            dayModel={model}
            onDayModelChanged={onUpdateDayModel}
            checked={!!existingModel}
            onCheckedChanged={(c) => {
                if (!c) {
                    onRemoveDayModel();
                } else {
                    onAddDayModel();
                }
            }} />
    }, [props])

    const mondayModel = useMemo(() => builder(DayOfWeek.Monday), [builder]);
    const tuesdayModel = useMemo(() => builder(DayOfWeek.Tuesday), [builder]);
    const wednesdayModel = useMemo(() => builder(DayOfWeek.Wednesday), [builder]);
    const thursdayModel = useMemo(() => builder(DayOfWeek.Thursday), [builder]);
    const fridayModel = useMemo(() => builder(DayOfWeek.Friday), [builder]);
    const saturdayModel = useMemo(() => builder(DayOfWeek.Saturday), [builder]);
    const sundayModel = useMemo(() => builder(DayOfWeek.Sunday), [builder]);

    return <Stack sx={{ pt: 4 }}>
        <Typography variant="subtitle2">{t("actionModules.schedulingStrategy.scheduleDefinition.days.title")}</Typography>
        <Typography variant="caption">{t("actionModules.schedulingStrategy.scheduleDefinition.days.description")}</Typography>
        {mondayModel}
        {tuesdayModel}
        {wednesdayModel}
        {thursdayModel}
        {fridayModel}
        {saturdayModel}
        {sundayModel}
    </Stack>
}

interface ScheduleDayFormGroupProps {
    dayModel: ScheduleDay;
    onDayModelChanged: (value: ScheduleDay) => void;

    checked: boolean;
    onCheckedChanged: (checked: boolean) => void;
}

function ScheduleDayFormGroup(props: ScheduleDayFormGroupProps) {
    const { t } = useTranslation();

    const dayLabel = {
        [DayOfWeek.Monday]: "days.Monday",
        [DayOfWeek.Tuesday]: "days.Tuesday",
        [DayOfWeek.Wednesday]: "days.Wednesday",
        [DayOfWeek.Thursday]: "days.Thursday",
        [DayOfWeek.Friday]: "days.Friday",
        [DayOfWeek.Saturday]: "days.Saturday",
        [DayOfWeek.Sunday]: "days.Sunday",
    }

    return <FormGroup sx={{ flexDirection: "row", gap: 2, pt: 1 }}>
        <FormControlLabel
            control={<Checkbox
                checked={props.checked}
                onChange={(e) => props.onCheckedChanged(e.target.checked)}
            />}
            sx={{ minWidth: 200, flex: 1 }}
            label={t(dayLabel[props.dayModel.dayOfWeek])} />

        <TimePicker
            value={props.dayModel.startTime}
            sx={{ minWidth: 100, flex: 1 }}
            disabled={!props.checked}
            onChange={(e) => props.onDayModelChanged({
                ...props.dayModel,
                startTime: e
            })}
            label={t("actionModules.schedulingStrategy.scheduleDefinition.days.start")}
        />

        <TimePicker
            value={props.dayModel.endTime}
            sx={{ minWidth: 100, flex: 1 }}
            disabled={!props.checked}
            onChange={(e) => props.onDayModelChanged({
                ...props.dayModel,
                endTime: e
            })}
            label={t("actionModules.schedulingStrategy.scheduleDefinition.days.end")}
        />
    </FormGroup>;
}
